import React, { useEffect, useState } from 'react';
import { adminCandidatesAPI } from '@/services/staffingAPI';
import { Search, Ban, RefreshCw, Loader2, UserCog, Calendar, Coins } from 'lucide-react';
import Pagination from '@/components/shared/Pagination';
import ScrollHint from '@/components/shared/ScrollHint';

export default function AdminCandidates() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState({ search: '', min_points: '', max_points: '', start_date: '', end_date: '', is_staffing_managed: '' });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, limit: pageSize };
      Object.entries(filters).forEach(([k, v]) => { if (v !== '') params[k] = v; });
      if (params.is_staffing_managed === 'true') params.is_staffing_managed = true;
      else if (params.is_staffing_managed === 'false') params.is_staffing_managed = false;
      const d = await adminCandidatesAPI.list(params);
      setRows(d.candidates || []); setTotal(d.total || 0);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load();}, [page, pageSize]);

  const toggle = async (r) => {
    try {
      if (r.is_disabled) await adminCandidatesAPI.restore(r.id);
      else { if (!window.confirm(`Revoke ${r.email}?`)) return; await adminCandidatesAPI.revoke(r.id); }
      await load();
    } catch (e) { alert(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div data-testid="admin-candidates">
      <h1 className="text-2xl font-semibold text-slate-900 dark:text-white mb-1 flex items-center gap-2"><UserCog className="w-5 h-5" /> All Candidates</h1>
      <p className="text-slate-500 dark:text-zinc-500 text-sm mb-5">{total} total · Filter, revoke, or restore any candidate profile</p>

      <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-xl p-4 mb-5 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-3 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-zinc-500" />
          <input value={filters.search} onChange={e => setFilters({...filters, search: e.target.value})}
            placeholder="Search by email…" className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg pl-10 pr-3 py-2 text-sm text-slate-900 dark:text-white"
            data-testid="admin-cand-search" />
        </div>
        <SmallField icon={Coins} label="Min points" type="number" value={filters.min_points} onChange={v => setFilters({...filters, min_points: v})} testId="min-points" />
        <SmallField icon={Coins} label="Max points" type="number" value={filters.max_points} onChange={v => setFilters({...filters, max_points: v})} testId="max-points" />
        <div>
          <label className="text-xs text-slate-500 dark:text-zinc-500 mb-1 block">Source</label>
          <select value={filters.is_staffing_managed} onChange={e => setFilters({...filters, is_staffing_managed: e.target.value})}
            className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white" data-testid="source-filter">
            <option value="">All</option>
            <option value="false">Independent</option>
            <option value="true">Staffing-managed</option>
          </select>
        </div>
        <SmallField icon={Calendar} label="From date" type="date" value={filters.start_date} onChange={v => setFilters({...filters, start_date: v})} testId="start-date" />
        <SmallField icon={Calendar} label="To date" type="date" value={filters.end_date} onChange={v => setFilters({...filters, end_date: v})} testId="end-date" />
        <div className="md:col-span-3 flex justify-end">
          <button onClick={() => { setPage(1); load(); }} className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-black rounded-full text-sm font-medium" data-testid="apply-filters">Apply filters</button>
        </div>
      </div>

      <ScrollHint>
      <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/60 rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="bg-white dark:bg-[#0e0e10] text-slate-500 dark:text-zinc-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Email</th>
              <th className="text-left p-3">Source</th>
              <th className="text-left p-3">Points</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Joined</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-6 text-center text-slate-500 dark:text-zinc-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-500 dark:text-zinc-500">No candidates match these filters.</td></tr>}
            {rows.map(r => (
              <tr key={r.id} className="border-t border-slate-200 dark:border-zinc-800/50" data-testid={`admin-cand-row-${r.id}`}>
                <td className="p-3 text-slate-900 dark:text-white">{r.name}</td>
                <td className="p-3 text-slate-500 dark:text-zinc-400 text-xs">{r.email}</td>
                <td className="p-3 text-xs">
                  {r.is_staffing_managed
                    ? <span className="px-1.5 py-0.5 bg-violet-500/15 text-violet-700 dark:text-violet-300 rounded">Staffing</span>
                    : <span className="px-1.5 py-0.5 bg-slate-200 dark:bg-zinc-700/40 text-slate-600 dark:text-zinc-300 rounded">Independent</span>}
                </td>
                <td className="p-3 text-slate-600 dark:text-zinc-300">{r.balance}</td>
                <td className="p-3 text-xs">
                  {r.is_disabled
                    ? <span className="text-red-700 dark:text-red-300">Revoked</span>
                    : <span className="text-emerald-700 dark:text-emerald-300">Active</span>}
                </td>
                <td className="p-3 text-slate-500 dark:text-zinc-500 text-xs">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}</td>
                <td className="p-3 text-right">
                  <button onClick={() => toggle(r)} className={`text-xs inline-flex items-center gap-1 ${r.is_disabled ? 'text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300' : 'text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300'}`} data-testid={`toggle-${r.id}`}>
                    {r.is_disabled ? <><RefreshCw className="w-3 h-3" /> Restore</> : <><Ban className="w-3 h-3" /> Revoke</>}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </ScrollHint>

      {total > 20 && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
          testIdPrefix="admin-cand-pg"
        />
      )}
    </div>
  );
}

function SmallField({ icon: Icon, label, value, onChange, type = 'text', testId }) {
  return (
    <div>
      <label className="text-xs text-slate-500 dark:text-zinc-500 mb-1 block flex items-center gap-1.5"><Icon className="w-3 h-3" /> {label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} data-testid={testId}
        className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white" />
    </div>
  );
}
